import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Flame, Trophy, Zap, Lock, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { pick, pickList } from "../lib/pick";
import SlideBlock from "../components/learn/SlideBlock";
import TheoryBlock from "../components/learn/TheoryBlock";
import CheckpointBlock from "../components/learn/CheckpointBlock";
import MatchBlock from "../components/learn/MatchBlock";
import OrderBlock from "../components/learn/OrderBlock";
import ScenarioBlock from "../components/learn/ScenarioBlock";
import VideoBlock from "../components/learn/VideoBlock";
import CoachCelebration from "../components/CoachCelebration";

const RENDERERS = {
  slide: SlideBlock,
  theory: TheoryBlock,
  checkpoint: CheckpointBlock,
  match: MatchBlock,
  order: OrderBlock,
  scenario: ScenarioBlock,
  video: VideoBlock,
};

export default function AcademyLesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useLang();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [streak, setStreak] = useState(0);
  const [result, setResult] = useState(null);
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState("");
  const [coach, setCoach] = useState(false);

  useEffect(() => {
    setIdx(0);
    setAnswers({});
    setStreak(0);
    setResult(null);
    setLocked("");
    api
      .get(`/academy/lessons/${lessonId}`)
      .then((r) => setData(r.data))
      .catch((e) => setLocked(formatApiError(e)));
  }, [lessonId]);

  const submit = async (finalAnswers) => {
    try {
      const { data: res } = await api.post(`/academy/lessons/${lessonId}/complete`, { answers: finalAnswers, lang });
      setResult(res);
      setCoach(true);
      if (res.xp_earned) toast.success(`+${res.xp_earned} XP`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDone = (value) => {
    const block = slides[idx];
    const merged = { ...answers, [String(idx)]: value };
    setAnswers(merged);
    if (block.type === "checkpoint") setStreak(value === block.correct_index ? streak + 1 : 0);
    if (idx + 1 >= slides.length) submit(merged);
    else setIdx(idx + 1);
  };

  const slides = data ? pickList(data.lesson, "slides", lang) : [];
  const bilingual = Boolean(data?.lesson?.slides_en?.length);

  const switchLang = (l) => {
    setLang(l);
    setIdx(0);
    setAnswers({});
    setStreak(0);
  };

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-24 pb-24 text-center" data-testid="academy-lesson-locked">
        <Lock className="w-10 h-10 text-[#1D4ED8] mx-auto" />
        <p className="text-base font-semibold text-[#0B1B3A] mt-5">{locked}</p>
        <Link to="/academy" className="btn-blue mt-7 inline-flex">{t("academy.backToCatalog")}</Link>
      </div>
    );
  }

  if (!data) return <p className="max-w-3xl mx-auto px-5 pt-20 text-[#64748B]">{t("common.loading")}</p>;

  const block = slides[idx];
  const Renderer = block ? RENDERERS[block.type] : null;
  const pct = Math.round((idx / Math.max(slides.length, 1)) * 100);

  return (
    <>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-10 pb-20" data-testid="academy-lesson-page">
        <CoachCelebration
          open={coach && Boolean(result)}
          onClose={() => setCoach(false)}
          title={t("coach.lessonDone")}
          subtitle={result ? `${pick(data.lesson, "title", lang)} · ${result.accuracy}%` : ""}
          badges={result?.badges || []}
          newBadges={result?.new_badges || []}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link data-testid="academy-lesson-back" to={`/academy/${data.course.slug}`} className="inline-flex items-center gap-2 text-sm font-bold text-[#334155] hover:text-[#1D4ED8] transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> {pick(data.course, "title", lang)}
          </Link>
          {bilingual && (
            <div className="inline-flex items-center gap-2 text-xs font-bold" data-testid="academy-lesson-lang-toggle">
              <Languages className="w-4 h-4 text-[#1D4ED8]" />
              {["el", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`academy-lesson-lang-${l}`}
                  onClick={() => switchLang(l)}
                  className={`px-3 py-1.5 rounded-full border transition-colors duration-200 ${lang === l ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "border-[#E2E8F0] text-[#334155] hover:border-[#1D4ED8]"}`}
                >
                  {l === "el" ? "Ελληνικά" : "English"}
                </button>
              ))}
            </div>
          )}
        </div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl mt-5 text-[#0B1B3A]" data-testid="academy-lesson-title">{pick(data.lesson, "title", lang)}</h1>
        <div className="flex flex-wrap items-center justify-between gap-4 mt-5">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#64748B]" data-testid="academy-lesson-progress">
            {t("academy.slide")} {Math.min(idx + 1, slides.length)}/{slides.length} · {t("learn.lessonNo")} {data.position}/{data.total_lessons}
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-bold text-[#1D4ED8]" data-testid="academy-lesson-streak">
            <Flame className="w-4 h-4" /> {streak}
          </span>
        </div>
        <div className="h-2.5 bg-[#EEF4FF] rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-[#1D4ED8] rounded-full transition-all duration-500" style={{ width: `${result ? 100 : pct}%` }} />
        </div>

        <div className="mt-8">
          {result ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="card-dark p-8 sm:p-10" data-testid="academy-lesson-result">
              <span className="badge-lime">{t("learn.completed")}</span>
              <p className="font-heading font-black text-5xl mt-5 text-[#0B1B3A]" data-testid="academy-result-score">{result.accuracy}%</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
                {[
                  { icon: Trophy, label: t("games.correct"), value: `${result.correct}/${result.total}` },
                  { icon: Zap, label: t("games.xpEarned"), value: `+${result.xp_earned}` },
                  { icon: Flame, label: t("gam.xp"), value: result.xp_total },
                ].map((s) => (
                  <div key={s.label} className="rounded-[20px] bg-[#EEF4FF] p-5">
                    <s.icon className="w-5 h-5 text-[#1D4ED8]" />
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#64748B] mt-3">{s.label}</p>
                    <p className="font-heading font-black text-xl text-[#0B1B3A] mt-1">{s.value}</p>
                  </div>
                ))}
              </div>
              {result.already_completed && <p className="text-xs text-[#EA580C] font-semibold mt-5">{t("learn.alreadyDone")}</p>}
              <div className="flex flex-wrap gap-3 mt-8">
                {data.next_lesson_id ? (
                  <button data-testid="academy-next-lesson-button" onClick={() => navigate(`/academy/lesson/${data.next_lesson_id}`)} className="btn-blue">
                    {t("learn.nextLesson")} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button data-testid="academy-final-quiz-button" onClick={() => navigate(`/academy/${data.course.slug}/quiz`)} className="btn-blue">
                    {t("academy.finalQuiz")} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ) : Renderer ? (
            <div className="slide-stack" data-testid="slide-stack"><Renderer key={`${lang}-${idx}`} block={block} onDone={handleDone} /></div>
          ) : (
            <p className="text-[#64748B]" data-testid="academy-lesson-empty">{t("learn.empty")}</p>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
